import { useCallback, useLayoutEffect, useRef, useState } from "react";

// Measures an element's height via ResizeObserver instead of reading
// offsetHeight during render (which forces a synchronous layout on every
// render). The returned ref is a callback ref so it also works with
// elements that mount/unmount conditionally.
const useElementHeight = <T extends HTMLElement = HTMLDivElement>(
  defaultHeight = 0
): [(node: T | null) => void, number] => {
  const elementRef = useRef<T | null>(null);
  const [node, setNode] = useState<T | null>(null);
  const [height, setHeight] = useState(defaultHeight);

  const ref = useCallback((el: T | null) => {
    elementRef.current = el;
    setNode(el);
  }, []);

  useLayoutEffect(() => {
    if (!node) return;
    setHeight(node.offsetHeight || defaultHeight);

    const observer = new ResizeObserver(() => {
      setHeight(node.offsetHeight || defaultHeight);
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [node]);

  return [ref, height];
};

export default useElementHeight;
